import {AccessibilityImages} from "../../../../../../../../../../../models/AccessibilityImages";
import React, {FC, useEffect, useState} from "react";
import {GridContextProvider, GridDropZone, GridItem, move, swap} from "react-grid-dnd";
import ImageItem from "./ImageItem";
import {Theme, Typography, useMediaQuery} from "@material-ui/core";
import Box from "@mui/material/Box";
import {styled} from "@mui/material";
import {useTranslation} from "react-i18next";


type CardType = {
  emptyText?: any
}
const Card = styled(Box)<CardType>`
  background: rgb(32, 45, 63);
  border: 1px solid rgba(238, 238, 238, 0.04);
  border-radius: 8px;
  position: relative;
  &.empty::before {
    content: ${(props) => `'${props.emptyText}'`};
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translateX(-50%);
    text-align: center;
  }
`

type PropsType = {
  images: AccessibilityImages[]
  onUpdateImages: (images: AccessibilityImages[]) => void
  onSetEditorMode: (data: AccessibilityImages) => void
  onSetCheckbox: (data: AccessibilityImages) => void
  onDeleteImage: (data: AccessibilityImages) => void
}

const GridDnd: FC<PropsType> = ({
  images,
  onUpdateImages,
  onSetEditorMode,
  onSetCheckbox,
  onDeleteImage,
}) => {
  const { t } = useTranslation()
  const [sections, setSections] = useState<any>({
    general: [],
    optional: []
  })

  useEffect(() => {
    if (images.length > 0) {
      setSections({
        general: images.filter((i) => i.is_general_image),
        optional: images.filter((i) => !i.is_general_image)
      })
    }
  }, [images]);

  const sm = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const md = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));

  const [gridOptions, setGridOptions] = useState({
    boxesPerRow: 5,
    rowHeight: 370,
  })

  useEffect(() => {
    if (md) {
      setGridOptions({
        boxesPerRow: 5,
        rowHeight: 350
      })
    }
    if (sm) {
      setGridOptions({
        boxesPerRow: 2,
        rowHeight: 350
      })
    }
  }, [sm, md]);

  const onChange = (sourceId: string, sourceIndex: number, targetIndex: number, targetId: string | undefined) => {
    if (targetId) {
      let result = move(
        sections[sourceId],
        sections[targetId],
        sourceIndex,
        targetIndex
      );

      if (sourceId === 'general') {
        if (result[0].length > 0) {
          result[0] = result[0].map((i: any) => ({
            ...i,
            is_general_image: true
          }))
        }
        if (result[1].length > 0) {
          result[1] = result[1].map((i: any) => ({
            ...i,
            is_general_image: false
          }))
        }
        onUpdateImages(result[0].concat(result[1]).map((image: any, index) => ({...image, order: index})));
      }
      if (sourceId === 'optional') {
        if (result[0].length > 0) {
          result[0] = result[0].map((i: any) => ({
            ...i,
            is_general_image: false
          }))
        }
        if (result[1].length > 0) {
          result[1] = result[1].map((i: any) => ({
            ...i,
            is_general_image: true
          }))
        }
        onUpdateImages(result[1].concat(result[0]).map((image: any, index) => ({...image, order: index})));
      }
      return setSections({
        ...sections,
        [sourceId]: result[0],
        [targetId]: result[1]
      });
    }

    const result = swap(sections[sourceId], sourceIndex, targetIndex);
    if (sourceId === 'general') {
      onUpdateImages(result.concat(sections.optional).map((image: any, index: number) => ({...image, order: index})))
    }
    if (sourceId === 'optional') {
      onUpdateImages(sections.general.concat(result).map((image: any, index: number) => ({...image, order: index})))
    }

    return setSections({
      ...sections,
      [sourceId]: result
    });
  }

  const calcHeight = (section: any[]) => {
    const length = section.length > 0 ? section.length : 1
    return gridOptions.rowHeight * Math.ceil(length / gridOptions.boxesPerRow)
  }

  return (
    <GridContextProvider onChange={onChange}>
      <Card
        pt={2}
        className={sections.general.length === 0 ? 'empty' : ''}
        emptyText={t('please_choose_a_main_unit_image')}
      >
        <Box mb={1} ml={2}>
          <Typography variant={'h4'}>{t('main_unit_image')}</Typography>
        </Box>
        <GridDropZone
          id="general"
          boxesPerRow={gridOptions.boxesPerRow}
          rowHeight={gridOptions.rowHeight}
          style={{ height: calcHeight(sections.general) }}
        >
          {sections.general.map((image: AccessibilityImages, index: number) => (
            <GridItem key={image.id}>
              <ImageItem
                item={image}
                index={index}
                onSetEditorMode={onSetEditorMode}
                onSetCheckbox={onSetCheckbox}
                onDeleteImage={onDeleteImage}
              />
            </GridItem>
          ))}
        </GridDropZone>
      </Card>
      <Card
        mt={3}
        pt={2}
        className={sections.optional.length === 0 ? 'empty' : ''}
        emptyText={t('please_choose_a_item_picture')}
      >
        <Box mb={1} ml={2}>
          <Typography variant={'h4'}>{t('item_pictures')}</Typography>
        </Box>
        <GridDropZone
          id="optional"
          boxesPerRow={gridOptions.boxesPerRow}
          rowHeight={gridOptions.rowHeight}
          style={{ height: calcHeight(sections.optional) }}
        >
          {sections.optional.map((image: AccessibilityImages, index: number) => (
            <GridItem key={image.id}>
              <ImageItem
                item={image}
                index={index}
                onSetEditorMode={onSetEditorMode}
                onSetCheckbox={onSetCheckbox}
                onDeleteImage={onDeleteImage}
              />
            </GridItem>
          ))}
        </GridDropZone>
      </Card>
    </GridContextProvider>
  )
}

export default GridDnd